import { useContext, ReactNode } from 'react';
import { FormikContext } from 'formik';
import Checkbox from '@mui/material/Checkbox';
import FormControl from '@mui/material/FormControl';
import FormControlLabel from '@mui/material/FormControlLabel';
import FormHelperText from '@mui/material/FormHelperText';
import { CheckBoxIcon } from '../../components/Icons';
import Typography from '../Typography';
import Tooltip from '../Tooltip';
import { FormTrackingContext } from '../Form';
import { CommonProps } from './types';
import { logFieldChange } from '../../utils/tracking';
import { COLORS } from '../../themev2';

type CheckboxFieldProps = Omit<CommonProps, 'name'> & {
  value?: boolean;
  name?: string;
  checked?: boolean;
  tooltip?: ReactNode;
  ariaLabel?: string;
};

export default function CheckboxField({
  error,
  name,
  label,
  value,
  onChange,
  checked = false,
  eventLogger = logFieldChange,
  disabled,
  fullWidth = true,
  tooltip,
  ariaLabel,
}: CheckboxFieldProps) {
  const formik = useContext(FormikContext);
  const _error = error || (name && formik?.touched[name] && formik?.errors[name]);
  const { trackingEvent } = useContext(FormTrackingContext);

  const checkboxControl = (
    <Checkbox
      checkedIcon={<CheckBoxIcon />}
      color="secondary"
      disabled={disabled}
      inputProps={ariaLabel ? { 'aria-label': ariaLabel } : undefined}
      checked={!!((name && formik?.values[name]) || checked || value)}
      onChange={(_, checked) => {
        !!name && formik && formik.setFieldValue(name, checked);
        onChange && onChange(checked);
        trackingEvent && name && eventLogger(trackingEvent, name, checked.toString());
      }}
      sx={{
        '&': {
          border: COLORS.gray[1000],
        },
        '&:hover': {
          background: 'none',
        },
      }}
    />
  );

  return (
    <FormControl error={!!_error} fullWidth={fullWidth}>
      <FormControlLabel
        control={
          tooltip ? (
            <Tooltip title={tooltip} placement="top">
              <span>{checkboxControl}</span>
            </Tooltip>
          ) : (
            checkboxControl
          )
        }
        label={<Typography sx={{ color: disabled ? '#9CABC2' : 'initial' }}>{label}</Typography>}
      />
      {!!_error && (
        <FormHelperText sx={{ color: 'inherit', whiteSpace: 'pre-line', fontSize: '14px' }}>
          {_error}
        </FormHelperText>
      )}
    </FormControl>
  );
}
